import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../api/authApi';
import type { AuthContextType, AuthMode, User } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const tokenStorageKey = 'serviceToken';

function getStoredToken(): string | null {
  return localStorage.getItem(tokenStorageKey) ?? sessionStorage.getItem(tokenStorageKey);
}

function storeToken(token: string, remember: boolean): void {
  if (remember) {
    localStorage.setItem(tokenStorageKey, token);
    sessionStorage.removeItem(tokenStorageKey);
    return;
  }

  sessionStorage.setItem(tokenStorageKey, token);
  localStorage.removeItem(tokenStorageKey);
}

function clearStoredToken(): void {
  localStorage.removeItem(tokenStorageKey);
  sessionStorage.removeItem(tokenStorageKey);
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authMode, setAuthMode] = useState<AuthMode | null>(null);
  const [isAuthModeLoading, setIsAuthModeLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = getStoredToken();
      const tokenCheck = token
        ? authApi
            .me()
            .then((userData) => {
              setUser(userData);
              setIsAuthenticated(true);
            })
            .catch(() => {
              clearStoredToken();
            })
        : Promise.resolve();

      const configCheck = authApi
        .getConfig()
        .then((config) => {
          setAuthMode(config.mode);
        })
        .catch(() => {
          setAuthMode('password');
        });

      await Promise.all([tokenCheck, configCheck]);
      setIsLoading(false);
      setIsAuthModeLoading(false);
    };
    initAuth();
  }, []);

  const login = async (credentials: { email: string; password: string; remember?: boolean }) => {
    const response = await authApi.login(credentials);
    storeToken(response.token, Boolean(credentials.remember));

    const userData = await authApi.me();
    setUser(userData);
    setIsAuthenticated(true);
  };

  const loginWithToken = async (token: string, remember = false) => {
    storeToken(token, remember);

    const userData = await authApi.me();
    setUser(userData);
    setIsAuthenticated(true);
  };

  const register = async (_email: string, _password: string, _firstname: string, _lastname: string) => {
    throw new Error('Not implemented');
  };

  const resetPassword = async (_email: string) => {
    await authApi.forgotPassword(_email);
  };

  const logout = () => {
    void authApi.logout().catch(() => {});
    clearStoredToken();
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoggedIn: isAuthenticated,
        isLoading,
        authMode,
        isAuthModeLoading,
        login,
        loginWithToken,
        register,
        resetPassword,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
